import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IoCheckmark, IoClose } from 'react-icons/io5';
import Header from 'components/Header';
import Table from 'components/Table';
import Loader from 'components/Loader';
import axios from 'config/axios';

import './index.css';

type QuizResult = {
    questionId: number;
    word: string;
    definition: string;
    sentence: string;
    selectedWord: string | null;
    correctWord: string;
    isCorrect: boolean;
    choices: string[];
};

type QuizData = {
    quizId: number;
    testNumber: number;
    attempt: number;
    score: number;
    correct: number;
    total: number;
    completedDate: string;
    studentName: string;
    studentOrganizationId: number | null;
    subject: string;
    results: QuizResult[];
};

type Filter = 'all' | 'correct' | 'incorrect';

function VocabQuizReport() {
    const [data, setData] = useState<QuizData | null>(null);
    const [filter, setFilter] = useState<Filter>('all');
    const { quizId } = useParams();

    useEffect(() => {
        axios.get(`vocab/tutor/quiz/${quizId}/`)
            .then(res => setData(res.data))
            .catch(error => console.error(error));
    }, [quizId]);

    if (!data) return <Loader />;

    const correctResults = data.results.filter(r => r.isCorrect);
    const wrongResults = data.results.filter(r => !r.isCorrect);
    const displayResults = filter === 'all'
        ? data.results
        : filter === 'correct'
            ? correctResults
            : wrongResults;

    return (
        <>
            <Helmet>
                <title>Vocab Quiz Report - Test Center Admin</title>
            </Helmet>
            <Header
                title={`${data.studentName} — Quiz ${data.testNumber}`}
                prevRoutes={[
                    { name: 'All Students', path: '/students' },
                    ...(data.studentOrganizationId
                        ? [{ name: data.studentName, path: `/students/${data.studentOrganizationId}` }]
                        : []
                    ),
                ]}
            />
            <div className='vqr'>
                {/* Summary row */}
                <div className='vqr__summary'>
                    <div className='vqr__summaryItem'>
                        <span className='vqr__summaryLabel'>Subject</span>
                        <span className='vqr__summaryValue'>{data.subject}</span>
                    </div>
                    <div className='vqr__summaryItem'>
                        <span className='vqr__summaryLabel'>Score</span>
                        <span className='vqr__summaryValue vqr__summaryValue--score'>{data.score}%</span>
                    </div>
                    <div className='vqr__summaryItem'>
                        <span className='vqr__summaryLabel'>Correct</span>
                        <span className='vqr__summaryValue'>{data.correct}/{data.total}</span>
                    </div>
                    <div className='vqr__summaryItem'>
                        <span className='vqr__summaryLabel'>Completed</span>
                        <span className='vqr__summaryValue'>{new Date(data.completedDate).toLocaleDateString()}</span>
                    </div>
                </div>

                {/* Filter tabs */}
                <div className='vqr__filters'>
                    <button
                        className={`vqr__filter ${filter === 'all' ? 'vqr__filter--active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All ({data.total})
                    </button>
                    <button
                        className={`vqr__filter ${filter === 'correct' ? 'vqr__filter--active vqr__filter--green' : ''}`}
                        onClick={() => setFilter('correct')}
                    >
                        Correct ({correctResults.length})
                    </button>
                    <button
                        className={`vqr__filter ${filter === 'incorrect' ? 'vqr__filter--active vqr__filter--red' : ''}`}
                        onClick={() => setFilter('incorrect')}
                    >
                        Incorrect ({wrongResults.length})
                    </button>
                </div>

                {/* Questions table */}
                <Table
                    className='vqr__table'
                    header={['', 'Sentence', 'Correct Answer', 'Student Answer', 'Definition']}
                    rows={displayResults.map(result => ([
                        result.isCorrect
                            ? <IoCheckmark className='vqr__iconCorrect' />
                            : <IoClose className='vqr__iconWrong' />,
                        result.sentence,
                        result.correctWord,
                        result.selectedWord || '—',
                        result.definition,
                    ]))}
                />
            </div>
        </>
    );
}

export default VocabQuizReport;
